"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { ChevronRight, Image as ImageIcon } from "lucide-react";
import NextImage from "next/image";
import { cn } from "../../lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Separator } from "../ui/separator";

// ACTION ROW STYLING
const actionRowVariants = cva(
  "group flex w-full items-center transition-all duration-200",
  {
    variants: {
      size: {
        default: "gap-4 py-4",
        compact: "gap-2 py-2",
      },
      variant: {
        default: "hover:bg-muted/50",
        card: [
          "rounded-lg border bg-card shadow-sm",
          "hover:scale-[1.01] hover:shadow-md",
        ],
      },
      clickable: {
        true: "cursor-pointer",
        false: "",
      },
    },
    compoundVariants: [
      {
        size: "default",
        variant: "card",
        class: "p-4 sm:p-5",
      },
      {
        size: "compact",
        variant: "card",
        class: "p-3",
      },
    ],
    defaultVariants: {
      size: "default",
      variant: "default",
      clickable: false,
    },
  }
);

// IMAGE STYLING
const actionRowImageVariants = cva(
  "relative shrink-0 overflow-hidden bg-muted object-cover",
  {
    variants: {
      imageStyle: {
        square: "aspect-square rounded-md",
        circle: "rounded-full",
      },
      size: {
        default: "size-12 sm:size-14",
        compact: "size-10 sm:size-12",
      },
    },
    defaultVariants: {
      imageStyle: "square",
      size: "default",
    },
  }
);

// TEXT STYLING
const actionRowTextVariants = cva("leading-tight tracking-tight", {
  variants: {
    textStyle: {
      title: "font-semibold",
      field: "font-medium",
    },
    size: {
      default: "text-base",
      compact: "text-sm",
    },
  },
  defaultVariants: {
    textStyle: "title",
    size: "default",
  },
});

// LABEL STYLING
const actionRowLabelVariants = cva("text-muted-foreground", {
  variants: {
    size: {
      default: "text-sm",
      compact: "text-xs",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

// TYPE DEFINITIONS
export interface ActionRowItem {
  // Accessibility
  ariaLabel?: string;

  // Action
  href?: string;

  // Identifiers
  id?: string | number;
  image?: string;
  imageAlt?: string;
  imagePosition?: "left" | "right";
  imageStyle?: "square" | "circle";
  label?: string;
  onClick?: () => void;
  target?: "_blank" | "_self";
  // Content
  text: string;

  // Options
  textStyle?: "title" | "field";
}

export interface ActionRowItemProps
  extends Pick<VariantProps<typeof actionRowVariants>, "size" | "variant"> {
  as?: "div" | "article";
  classNames?: {
    root?: string;
    image?: string;
    content?: string;
    chevron?: string;
  };
  imagePosition?: "left" | "right";
  imageStyle?: "square" | "circle";
  item: ActionRowItem;
  onClick?: () => void;
  showChevron?: boolean;
  textStyle?: "title" | "field";
}

export function ActionRowItem({
  item,
  size = "default",
  variant = "default",
  textStyle = "title",
  imageStyle = "square",
  imagePosition = "left",
  showChevron = false,
  onClick,
  as: Element = "article",
  classNames,
}: ActionRowItemProps) {
  const finalTextStyle = item.textStyle ?? textStyle;
  const finalImageStyle = item.imageStyle ?? imageStyle;
  const finalImagePosition = item.imagePosition ?? imagePosition;
  const finalOnClick = item.onClick ?? onClick;

  // Determine if this should be a link or clickable element
  const isLink = Boolean(item.href);
  const isClickable = Boolean(finalOnClick) || isLink;

  // Render image section
  const renderImage = () => {
    if (!item.image) {
      return null;
    }

    // Use Avatar for circular images
    if (finalImageStyle === "circle") {
      return (
        <div
          className={cn(
            actionRowImageVariants({ imageStyle: "circle", size }),
            classNames?.image
          )}
        >
          <Avatar className="size-full">
            <AvatarImage alt={item.imageAlt ?? item.text} src={item.image} />
            <AvatarFallback>
              <ImageIcon className="size-5 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
        </div>
      );
    }

    // Use Next.js Image for square images
    return (
      <div
        className={cn(
          actionRowImageVariants({ imageStyle: "square", size }),
          classNames?.image
        )}
      >
        <NextImage
          alt={item.imageAlt ?? item.text}
          className="object-cover"
          fill
          sizes="(max-width: 640px) 40px, 56px"
          src={item.image}
        />
      </div>
    );
  };

  // Render chevron for navigation hint
  const renderChevron = () => {
    if (!(showChevron || isLink)) {
      return null;
    }

    return (
      <ChevronRight
        aria-hidden="true"
        className={cn(
          "size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1",
          classNames?.chevron
        )}
      />
    );
  };

  // Build the action row content
  const actionRowContent = (
    <>
      {/* Left: Image */}
      {finalImagePosition === "left" && renderImage()}

      {/* Center: Content stack */}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-1",
          classNames?.content
        )}
      >
        {/* Text */}
        <div
          className={cn(
            actionRowTextVariants({ textStyle: finalTextStyle, size }),
            "truncate"
          )}
        >
          {item.text}
        </div>

        {/* Label */}
        {item.label && (
          <div className={cn(actionRowLabelVariants({ size }), "truncate")}>
            {item.label}
          </div>
        )}
      </div>

      {/* Right: Image (if position is right) */}
      {finalImagePosition === "right" && renderImage()}

      {/* Chevron indicator */}
      {renderChevron()}
    </>
  );

  // Wrapper class
  const actionRowClassName = cn(
    actionRowVariants({ size, variant, clickable: isClickable }),
    classNames?.root
  );

  // Render as link
  if (isLink && item.href) {
    return (
      <a
        aria-label={item.ariaLabel ?? `Navigate to ${item.text}`}
        className={actionRowClassName}
        href={item.href}
        onClick={finalOnClick}
        rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
        target={item.target}
      >
        {actionRowContent}
      </a>
    );
  }

  // Render as clickable button
  if (isClickable && finalOnClick) {
    return (
      <button
        aria-label={item.ariaLabel ?? item.text}
        className={actionRowClassName}
        onClick={finalOnClick}
        type="button"
      >
        {actionRowContent}
      </button>
    );
  }

  // Render as static element
  return (
    <Element
      aria-label={item.ariaLabel ?? item.text}
      className={actionRowClassName}
    >
      {actionRowContent}
    </Element>
  );
}

// Collection component for multiple action row items
export interface ActionRowCollectionProps
  extends Pick<VariantProps<typeof actionRowVariants>, "size" | "variant"> {
  // Styling
  className?: string;
  imagePosition?: "left" | "right";
  imageStyle?: "square" | "circle";
  items: ActionRowItem[];

  // Display
  limit?: number;

  // Interaction
  onItemClick?: (item: ActionRowItem, index: number) => void;
  showChevron?: boolean;

  // Shared defaults (cascade to items)
  textStyle?: "title" | "field";
}

export function ActionRowCollection({
  items,
  size = "default",
  variant = "default",
  textStyle = "title",
  imageStyle = "square",
  imagePosition = "left",
  showChevron = false,
  limit,
  onItemClick,
  className,
}: ActionRowCollectionProps) {
  // Apply limit if specified
  const displayedItems = limit ? items.slice(0, limit) : items;

  // Empty state
  if (displayedItems.length === 0) {
    return null;
  }

  return (
    <div className={cn("w-full", className)} data-slot="action-row-collection">
      {displayedItems.map((item, index) => (
        <div key={item.id ?? `action-row-${index}`}>
          <ActionRowItem
            imagePosition={imagePosition}
            imageStyle={imageStyle}
            item={item}
            onClick={onItemClick ? () => onItemClick(item, index) : undefined}
            showChevron={showChevron}
            size={size}
            textStyle={textStyle}
            variant={variant}
          />
          {/* Add separator between items (default variant only) */}
          {variant === "default" && index < displayedItems.length - 1 && (
            <Separator />
          )}
        </div>
      ))}
    </div>
  );
}
